import Axios from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import qs from 'query-string';

import { getAuth } from '~/firebase/fbAuth';
import { fireCaWiseHotelTag } from '~/js-modules/caWiseTag';
import { ENVIRONMENT, isServer } from '~/shared/utils/envUtil';
import { logger } from '~/utils/loggerUtil';
import { sendOrderFormSubmitLog } from '~/utils/logging';

import { GetConfigUtil } from '../shared/utils/envUtil';
import { LoggingCategory, LoggingLabel } from './constants';
import { sleep } from './logic';

dayjs.extend(utc);

const ENV_CONFIG = GetConfigUtil.getPublicStaticConfig();

const ParamKeys = {
  utmContent: 'utm_content',
  utmSource: 'utm_source',
  utmMedium: 'utm_medium',
  utmCampaign: 'utm_campaign',
};

export const getGAParams = () => {
  if (isServer) {
    return undefined;
  }

  const params = qs.parse(window.location.search);

  return {
    utmContent: params[ParamKeys.utmContent]?.toString() ?? '',
    utmSource: params[ParamKeys.utmSource]?.toString() ?? '',
    utmMedium: params[ParamKeys.utmMedium]?.toString() ?? '',
    utmCampaign: params[ParamKeys.utmCampaign]?.toString() ?? '',
  };
};

export const getClientId = (): string => {
  try {
    // @ts-ignore: GTM用
    // eslint-disable-next-line no-undef
    const clientId = google_tag_manager['GTM-PQNB8DN'].dataLayer.get(
      'clientId'
    );
    return typeof clientId === 'string' ? clientId : '';
  } catch (e) {
    return '';
  }
};

const getUserId = (): string | undefined => {
  if (isServer || typeof window === 'undefined') return undefined;

  // Firebase Authからユーザを取得
  const currentUser = getAuth().currentUser;
  return currentUser?.isAnonymous ? undefined : currentUser?.uid;
};

/**
 * データレイヤーを使ったイベントのトラッキング
 */
export const sendDataLayerEvent = (eventName: string, ...parameters) => {
  try {
    if (isServer || typeof window === 'undefined') {
      return;
    }
    const originUrl = window.location.href;
    const dataLayer = (window as any).dataLayer;
    const userId = getUserId();

    const sendData = Object.assign(
      {},
      {
        event: eventName,
        pageUrlWithQueryParameter: originUrl,
      },
      // NOTE: (@kazutoyo) ログインしているとき（匿名ユーザではないときに）FirebaseのユーザIDをセット
      {
        userId: userId,
        crmId: userId,
      },
      ...parameters
    );
    logger.debug('sendDataLayerEvent', JSON.stringify(sendData));
    dataLayer?.push(sendData);
  } catch (e) {
    // sendDataLayerEventの失敗はログを吐いて握りつぶす
    logger.error(e);
    return Promise.resolve();
  }
};

/**
 * GAの拡張eコマースのCVトラッキング
 */
const fireGoogleEnhancedEcommerceTag = (
  orderId: string,
  totalPrice: string
) => {
  if (isServer || typeof window === 'undefined') {
    return;
  }
  const dataLayer = (window as any).dataLayer;
  const sendData = {
    ecommerce: {
      currencyCode: 'JPY',
      purchase: {
        actionField: {
          id: orderId,
          revenue: totalPrice,
        },
      },
    },
  };

  if (ENV_CONFIG.ENVIRONMENT !== ENVIRONMENT.PRODUCTION) {
    logger.debug(
      'fireGoogleEnhancedEcommerceTag run, but not requesting to server for not production. data: ',
      JSON.stringify(sendData)
    );
    return;
  }

  logger.debug('fireGoogleEnhancedEcommerceTag', JSON.stringify(sendData));
  dataLayer?.push(sendData);
};

/**
 * データフィード広告用のトラッキング(CV以外)
 */
const fireGoogleDynamicRemarkTag = (
  userAction: 'view_item' | 'add_to_cart',
  itemIds: string[],
  totalPrice: number
) => {
  if (isServer || typeof window === 'undefined') {
    return;
  }
  if (typeof gtag === 'undefined') {
    logger.error('fail to fire viewItem tag. gtag is not found.');
    return;
  }

  const params = {
    send_to: ENV_CONFIG.GOOGLE_ADS_ID,
    value: totalPrice,
    currency: 'JPY',
    items: itemIds.map(itemId => ({
      id: itemId,
      google_business_vertical: 'hotel_rental',
    })),
  };

  if (ENV_CONFIG.ENVIRONMENT !== ENVIRONMENT.PRODUCTION) {
    logger.debug(
      'fireGoogleDynamicRemarkTag run, but not requesting to server for not production. data: ',
      {
        userAction,
        params,
      }
    );
    return;
  }

  try {
    // eslint-disable-next-line no-undef
    gtag('event', userAction, params);
  } catch (e) {
    logger.error(e);
  }
};

/**
 * CV時のデータフィード/HotelAds/Google広告(hotspring)のトラッキング
 * @param itemIds
 * @param totalPrice
 * @param orderId
 * @param checkInDate
 * @param checkOutDate
 */
const fireGoogleCvTag = (
  itemIds: string[],
  totalPrice: number,
  orderId: string,
  checkInDate: Dayjs,
  checkOutDate: Dayjs
) => {
  if (isServer || typeof window === 'undefined') {
    return;
  }
  if (typeof gtag === 'undefined') {
    logger.error('fail to fire viewItem tag. gtag is not found.');
    return;
  }

  const userAction = 'purchase';
  const _checkInDate = convertDate('under-score', checkInDate); // YYYY_MM_DD
  const _checkOutDate = convertDate('under-score', checkOutDate); // YYYY_MM_DD

  const params = {
    send_to: [
      ENV_CONFIG.GOOGLE_ADS_ID,
      'AW-622270959/u_-mCPTkldQBEO-z3KgC', // Hotel Ads
      'HA-1330421914', // Hotel Ads
      'AW-824118544/2LEeCPeXg3sQkJr8iAM', // Google広告(hotspring)
    ],
    transaction_id: orderId,
    value: totalPrice,
    currency: 'JPY',
    items: itemIds.map(itemId => ({
      id: itemId,
      google_business_vertical: 'hotel_rental',
      start_date: _checkInDate,
      end_date: _checkOutDate,
    })),
  };

  if (ENV_CONFIG.ENVIRONMENT !== ENVIRONMENT.PRODUCTION) {
    logger.debug(
      'fireGoogleDynamicRemarkTag run, but not requesting to server for not production. data: ',
      {
        userAction,
        params,
      }
    );
    return;
  }

  try {
    // eslint-disable-next-line no-undef
    gtag('event', userAction, params);
  } catch (e) {
    logger.error(e);
  }
};

const fireFacebookDynamicRemarkTag = (
  userAction: 'ViewContent' | 'AddToCart' | 'Purchase',
  itemIds: string[],
  totalPrice: number
) => {
  if (isServer || typeof window === 'undefined') {
    return;
  }

  const params = {
    value: totalPrice,
    currency: 'JPY',
    content_ids: itemIds,
    content_type: 'product',
  };

  if (ENV_CONFIG.ENVIRONMENT !== ENVIRONMENT.PRODUCTION) {
    logger.debug(
      'fireFacebookDynamicRemarkTag run, but not requesting to server for not production. data: ',
      {
        userAction,
        params,
      }
    );
    return;
  }

  try {
    // eslint-disable-next-line no-undef
    fbq
      ? // eslint-disable-next-line no-undef
        fbq('track', userAction, params)
      : logger.error('fail to fire viewItem tag. facebook tag is not found.');
  } catch (e) {
    logger.error(e);
  }
};

const viewItem = (cocolocalaHotelIds: string[], lowestPrice: number) => {
  fireGoogleDynamicRemarkTag('view_item', cocolocalaHotelIds, lowestPrice);
  fireFacebookDynamicRemarkTag('ViewContent', cocolocalaHotelIds, lowestPrice);
};
const addToCart = (cocolocalaHotelIds: string[], selectedRoomPrice: number) => {
  fireGoogleDynamicRemarkTag(
    'add_to_cart',
    cocolocalaHotelIds,
    selectedRoomPrice
  );
  fireFacebookDynamicRemarkTag(
    'AddToCart',
    cocolocalaHotelIds,
    selectedRoomPrice
  );
};

/**
 * フォルシアのCVタグ
 * @param hotelId ホテルID (ホテルマスタのhotelcode と同じもの)
 * @param price 予約した価格(数字列:例 14800)
 * @param orderId 予約番号(英数字列)
 * @param checkInDate チェックイン日
 * @param checkOutDate チェックアウト日
 * @param nights 宿泊日数(数字列: 例 1)
 * @param numberOfPeople 1室人数
 */
const fireForciaCvTag = (
  hotelId: string,
  price: string,
  orderId: string,
  checkInDate: Dayjs,
  checkOutDate: Dayjs,
  nights: string,
  numberOfPeople: string
) => {
  const _checkInDate = convertDate('none', checkInDate); // YYYYMMDD:例 20180801
  const _checkOutDate = convertDate('none', checkOutDate); // YYYYMMDD:例 20180801
  const sendData = {
    site_code: 'hsp',
    base_price: price,
    booking_xref: orderId,
    checkin_date: _checkInDate,
    checkout_date: _checkOutDate,
    length_of_stay: nights,
    hotel_id: hotelId,
    pax: numberOfPeople,
  };

  // @ts-ignore
  window._f_data = sendData;

  if (ENV_CONFIG.ENVIRONMENT === ENVIRONMENT.PRODUCTION) {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = 'https://hpa.forcia.jp/loa/script/forciatag.js';
    document.body.appendChild(script);
  } else {
    logger.debug(
      'ForciaTag run, but not requesting to server for not production. data: ',
      sendData
    );
  }
};

const fireValueCommerceItagJs = (
  orderId: string,
  userId: string,
  amount: string
) => {
  const div = document.createElement('div');
  div.dataset.vcEcId = ENV_CONFIG.VALUE_COMMERCE.EC_ID; // 広告主ID
  div.dataset.vcSalesNumber = '1'; // 申込件数
  div.dataset.vcSalesAmount = amount;
  div.dataset.vcOrderId = orderId;
  div.dataset.vcUid = userId;
  // 以下現状使っていないパラメータ
  // div.dataset.vcSalesName = '';
  // div.dataset.vcSalesEmail = '';
  // div.dataset.vcPrgrid = '';
  // div.dataset.vcCs = '';

  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.src = '//cv.valuecommerce.com/vccv.min.js';

  document.body.appendChild(div);
  document.body.appendChild(script);
};

/**
 * ValueCommerceのCVタグと注文DB作成のための処理。内部に非同期処理を含むが、ブロックさせずにエラーもログ吐いて殺している
 * @param cocolocalaUserId
 * @param orderId 予約番号
 * @param totalAmount
 */
const fireValueCommerceCvTag = async (
  cocolocalaUserId: string,
  orderId: string,
  totalAmount: number
) => {
  fireValueCommerceItagJs(orderId, cocolocalaUserId, totalAmount.toString());
  const kickbackUrl = '/_cw/vc/kickback';
  const kickbackPostData: {
    orderId: string;
    cocolocalaUserId: string;
    totalAmount: string;
  } = {
    orderId: orderId,
    cocolocalaUserId: cocolocalaUserId.toString(),
    totalAmount: totalAmount.toString(),
  };

  return Axios.post(kickbackUrl, kickbackPostData);
};

const fireA8CvTag = (hotelId: string, price: number, orderId: string) => {
  // 現在の販売状況から、プラン売りでなかったらホテル売り、というロジックにしている
  const a8Item: {
    code: string; // 商品を識別するコード。省略時は[a8]となる。また50byteを超える場合は、50byte目以降が切り捨てられ成果として計上される。
    price: number; // 商品単価。小数点以下第二位まで受け入れる。(レポート上は[currency]の設定値に基づいて[JPY]へ計算されたあと、小数点以下を切り落とされる)
    quantity: 1; // 商品個数。フォーマットエラー時は[1]となる。
  } = {
    code: `hotel_${hotelId}`,
    price: price,
    quantity: 1,
  };

  const sendData = {
    pid: ENV_CONFIG.A8_PROGRAM_ID, // A8.net発行のプログラムID (PGID)
    order_number: orderId, // 注文情報を識別するID。A8.netでは「注文番号」としてレポートされる。省略時には[null-タイムスタンプ-乱数]という文字列を自動生成する。また、50byteを超える場合は、50byte以降が切り捨てられ計上される。
    currency: 'JPY', // 通貨コード。JPY/USD/EURが利用可能。 空欄/未設定/対応外通貨コードの場合は[JPY]
    items: [a8Item],
    total_price: price, // 省略時は[price] [quantity] [currency] から[JPY]で自動的に計算される。指定の値が同様の計算結果と異なる場合、計算結果が優先される。
  };

  try {
    if (ENV_CONFIG.ENVIRONMENT === ENVIRONMENT.PRODUCTION) {
      // @ts-ignore
      window.a8sales(sendData);
    } else {
      logger.debug(
        'A8Tag run, but not requesting to server for not production. data: ',
        sendData
      );
    }
  } catch (e) {
    logger.error(e);
  }
};

/**
 * LINEトラベルのタグ発火
 * @param planId
 * @param hotelId
 * @param price
 * @param orderId
 * @param checkInDate
 * @param checkOutDate
 * @param internationalOrDomestic
 * @param planTitle
 * @param numberOfAdults
 * @param numberOfChildren
 * @param numberOfBabies
 * @param hotelName
 * @param mealLabel
 */
const fireLineCvTag = (
  hotelId: string,
  price: number,
  orderId: string,
  checkInDate: Dayjs,
  checkOutDate: Dayjs,
  numberOfAdults: number,
  numberOfChildren: number,
  numberOfBabies: number,
  hotelName: string,
  mealLabel: string
) => {
  // LINE TRAVEL(ホテル単体向け)のCVタグを発火

  try {
    if (ENV_CONFIG.ENVIRONMENT === ENVIRONMENT.PRODUCTION) {
      fireCaWiseHotelTag(
        window,
        document,
        orderId,
        hotelId,
        price,
        numberOfAdults,
        numberOfChildren,
        numberOfBabies,
        hotelName,
        mealLabel,
        checkInDate,
        checkOutDate
      );
    } else {
      logger.debug(
        'LINE HotelTag run, but not requesting to server for not production. data: )',
        {
          orderId,
          hotelId,
          price,
          numberOfAdults,
          numberOfChildren,
          numberOfBabies,
          hotelName,
          mealLabel,
          checkInDate,
          checkOutDate,
        }
      );
    }
  } catch (e) {
    logger.error(e);
  }
};

const convertDate = (
  type: 'under-score' | 'hyphen' | 'none',
  date: Dayjs
): string => {
  let format: string;
  if (type === 'under-score') {
    format = 'YYYY_MM_DD';
  } else if (type === 'hyphen') {
    format = 'YYYY-MM-DD';
  } else if (type === 'none') {
    format = 'YYYYMMDD';
  } else {
    format = 'YYYY-MM-DD';
  }
  return date.utcOffset(540).format(format);
};

// TODO: 今は全く待たないで処理が進んでいくので、厳密ではない。いずれPromimse.allなどで制御したい
/**
 * 購入時のデータ送信処理。非同期でブロックせず流れていくで良いため、現状特にawaitしたりしていない。
 * @param cocolocalaHotelId
 * @param totalAmount
 * @param orderId
 * @param travelId
 * @param checkInDate
 * @param checkOutDate
 * @param nights
 * @param numberOfPeople
 * @param numberOfAdults
 * @param numberOfChildren
 * @param numberOfBabies
 * @param cocolocalaUserId
 * @param firebaseUserId
 * @param hotelName
 * @param withMeal
 * @param productType
 * @param email
 * @param dPFlightInternationalVersion
 */
const purchase = (
  cocolocalaHotelId: string,
  totalAmount: number,
  orderId: string,
  travelId: string,
  checkInDate: dayjs.Dayjs,
  checkOutDate: dayjs.Dayjs,
  nights: number,
  numberOfPeople: number,
  numberOfAdults: number,
  numberOfChildren: number,
  numberOfBabies: number,
  cocolocalaUserId: string,
  firebaseUserId: string,
  hotelName: string,
  mealLabel: string,
  productType: string,
  email: string,
  dPFlightInternationalVersion?: string
) => {
  fireGoogleCvTag(
    [cocolocalaHotelId],
    totalAmount,
    orderId,
    checkInDate,
    checkOutDate
  );

  fireFacebookDynamicRemarkTag('Purchase', [cocolocalaHotelId], totalAmount);

  fireForciaCvTag(
    cocolocalaHotelId,
    totalAmount.toString(),
    orderId,
    checkInDate,
    checkOutDate,
    nights.toString(),
    numberOfPeople.toString()
  );

  fireValueCommerceCvTag(cocolocalaUserId, orderId, totalAmount).catch(e =>
    logger.error(e)
  );

  fireGoogleEnhancedEcommerceTag(orderId, totalAmount.toString());

  // 申し込み成功ログ
  sendDataLayerEvent(LoggingCategory.SUCCEED_IN_SUBMITTING_FORM, {
    travelId,
    orderId: Number(orderId),
    totalAmount,
    productType,
    dPFlightInternationalVersion,
    email,
  });

  sendOrderFormSubmitLog(
    LoggingLabel.ORDER_FORM_SUBMIT_SUCCESS,
    cocolocalaUserId.toString(),
    firebaseUserId
  ).catch(e => logger.error('fail logging', e));

  fireLineCvTag(
    cocolocalaHotelId,
    totalAmount,
    orderId,
    checkInDate,
    checkOutDate,
    numberOfAdults,
    numberOfChildren,
    numberOfBabies,
    hotelName,
    mealLabel
  );
};

export const isTagInitCompleted = (): boolean => {
  return typeof gtag !== 'undefined' && typeof fbq !== 'undefined';
};

// TODO: FBとGoogleがそれぞれ準備できた順に発火し、両方が揃わないと発火しないような依存にしないようにする
export const waitForTagInit = async (): Promise<void> => {
  const check = async (): Promise<void> => {
    if (isTagInitCompleted()) {
      return;
    } else {
      await sleep(500);
      return check();
    }
  };

  await check();
  return;
};

export const measurementTag = {
  viewItem,
  addToCart,
  purchase,
};
